import { useTranslate } from "ra-core";
import type { RefObject } from "react";

import { Spinner } from "@/components/admin/spinner";
import { Button } from "@/components/ui/button";

import { useLoadMoreOnScroll } from "./useLoadMoreOnScroll";

export interface LoadMoreProps {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
  /** Scrolling element containing the list; the viewport when omitted. */
  rootRef?: RefObject<HTMLElement | null>;
}

/**
 * End-of-list sentinel: loads the next page as soon as it scrolls into view.
 * The button is a fallback for keyboard users and for browsers that do not
 * report the intersection (e.g. a background tab).
 */
export const LoadMore = ({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  rootRef,
}: LoadMoreProps) => {
  const translate = useTranslate();
  const sentinelRef = useLoadMoreOnScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    rootRef,
  });

  if (!hasNextPage) return null;

  return (
    <div ref={sentinelRef} className="flex min-h-8 justify-center py-1">
      {isFetchingNextPage ? (
        <Spinner />
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-xs text-muted-foreground"
          onClick={() => fetchNextPage()}
        >
          {translate("crm.operations.load_more")}
        </Button>
      )}
    </div>
  );
};
